import React, { useState } from 'react';

const AUXILIARY_ITEMS = [
    { id: 'header', label: 'Header' },
    { id: 'header_image', label: 'Header Image' },
    { id: 'footer', label: 'Footer' },
    { id: 'footer_image', label: 'Footer Image' },
    { id: 'number', label: 'Page Number' },
    { id: 'footnote', label: 'Footnote' },
    { id: 'formula_number', label: 'Formula Number' },
    { id: 'aside_text', label: 'Aside Text' },
];

const SHAPE_OPTIONS = ['auto', 'rect', 'quad', 'poly'];
const SHAPE_LABELS = { auto: 'Auto', rect: 'Rectangle', quad: 'Quadrilateral', poly: 'Polygon' };

const PROMPT_OPTIONS = [
    { value: 'ocr', label: 'Text' },
    { value: 'formula', label: 'Formula' },
    { value: 'table', label: 'Table' },
    { value: 'chart', label: 'Chart' },
    { value: 'seal', label: 'Seal' },
    { value: 'spotting', label: 'Text Spotting' },
];

const Tip = ({ text }) => (
    <span className="ocr-tip-wrap" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: '50%', border: '1px solid #c8cce0', color: '#9498ac', fontSize: '9px', cursor: 'help', flexShrink: 0 }}>
        ?
        <span className="ocr-tip-text">{text}</span>
        <style>{`
            .ocr-tip-wrap .ocr-tip-text {
                display: none;
                position: absolute;
                bottom: calc(100% + 6px);
                left: 50%;
                transform: translateX(-50%);
                background: #140e35;
                color: #fff;
                font-size: 11px;
                font-weight: 400;
                line-height: 1.4;
                padding: 6px 10px;
                border-radius: 6px;
                white-space: nowrap;
                max-width: 260px;
                white-space: normal;
                z-index: 100;
                pointer-events: none;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            }
            .ocr-tip-wrap .ocr-tip-text::after {
                content: '';
                position: absolute;
                top: 100%;
                left: 50%;
                transform: translateX(-50%);
                border: 5px solid transparent;
                border-top-color: #140e35;
            }
            .ocr-tip-wrap:hover .ocr-tip-text {
                display: block;
            }
        `}</style>
    </span>
);

function Toggle({ label, checked, onChange, tip, req }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', minHeight: '28px' }}>
            <span style={{ fontSize: '12px', color: '#140e35', display: 'flex', alignItems: 'center', gap: '3px' }}>
                {label}
                {req && <span style={{ color: '#FF4040', fontSize: '10px' }}>*</span>}
                {tip && <Tip text={tip} />}
            </span>
            <button onClick={() => onChange(!checked)} style={{ width: '36px', height: '18px', borderRadius: '9px', border: 'none', cursor: 'pointer', background: checked ? '#4658ff' : '#d9dee9', position: 'relative', transition: 'background 0.2s', padding: 0, flexShrink: 0 }}>
                <span style={{ position: 'absolute', top: '1.5px', left: checked ? '18px' : '1.5px', width: '15px', height: '15px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.15)', transition: 'left 0.15s' }} />
            </button>
        </div>
    );
}

function SliderField({ label, value, onChange, min, max, step, tip }) {
    const decimals = step < 1 ? Math.ceil(-Math.log10(step)) : 0;
    return (
        <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '12px', color: '#140e35', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                {label} {tip && <Tip text={tip} />}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="number" value={typeof value === 'number' ? (decimals > 0 ? value.toFixed(decimals) : value) : value} onChange={e => onChange(Number(e.target.value))} step={step} min={min} max={max} style={{ width: '70px', padding: '3px 6px', border: '1px solid #d9dee9', borderRadius: '4px', fontSize: '12px', color: '#140e35', outline: 'none' }} />
                <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} style={{ flex: 1, accentColor: '#4658ff', height: '3px' }} />
            </div>
        </div>
    );
}

export default function OcrSettings({ settings, onChange }) {
    const set = (key, value) => onChange({ ...settings, [key]: value });
    const [showAdvanced, setShowAdvanced] = useState(false);

    const handlePromptChange = (value) => {
        const updates = { promptLabel: value };
        if (value === 'spotting') updates.useLayoutDetection = false;
        onChange({ ...settings, ...updates });
    };

    const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' };
    const divider = { height: '1px', background: '#e1e6f8', margin: '14px 0' };
    const sectionTitle = { fontSize: '13px', fontWeight: 600, color: '#140e35', marginBottom: '10px' };
    const radioGroup = { display: 'flex', gap: '14px', flexWrap: 'wrap' };
    const radioLabel = { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#565772', cursor: 'pointer' };

    return (
        <div style={{ fontSize: '12px' }}>
            {/* Auxiliary Content Parsing */}
            <div style={sectionTitle}>Auxiliary Content Parsing</div>
            <div style={{ fontSize: '10px', color: '#aeb4c9', marginBottom: '8px' }}>
                The model automatically identifies and filters auxiliary content; parsing will resume once this option is enabled
            </div>
            <div style={grid2}>
                {AUXILIARY_ITEMS.map(item => (
                    <Toggle key={item.id} label={item.label} checked={settings.auxiliary?.[item.id] ?? true} onChange={v => set('auxiliary', { ...settings.auxiliary, [item.id]: v })} />
                ))}
            </div>

            <div style={divider} />

            {/* Model Parameter Settings */}
            <div style={sectionTitle}>Model Parameter Settings</div>
            <div style={grid2}>
                <Toggle label="Image Orientation Correction" checked={settings.useDocPreprocessor} onChange={v => set('useDocPreprocessor', v)} req tip="Enable document orientation classification and correction" />
                <Toggle label="Image Distortion Correction" checked={settings.useDocUnwarping} onChange={v => set('useDocUnwarping', v)} tip="Enable document unwarping/dewarping" />
                <Toggle label="Layout Analysis" checked={settings.useLayoutDetection} onChange={v => set('useLayoutDetection', v)} tip="Enable layout detection using PP-DocLayoutV3" />
                <Toggle label="Chart Recognition" checked={settings.useChartRecognition} onChange={v => set('useChartRecognition', v)} tip="Enable chart/graph structure recognition" />
                <Toggle label="Seal Recognition" checked={settings.useSealRecognition} onChange={v => set('useSealRecognition', v)} tip="Enable seal/stamp recognition" />
                <Toggle label="Image Text Recognition" checked={settings.useOcrForImageBlock} onChange={v => set('useOcrForImageBlock', v)} tip="Run OCR on image blocks within documents" />
                <Toggle label="Merge tables across pages" checked={settings.mergeTables} onChange={v => set('mergeTables', v)} tip="Merge table structures that span multiple pages" />
                <Toggle label="Paragraph title level recognition" checked={settings.relevelTitles} onChange={v => set('relevelTitles', v)} tip="Reassign title hierarchy levels across pages" />
            </div>

            <div style={divider} />

            {/* Geometric Shape */}
            <div style={{ fontSize: '12px', color: '#140e35', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                Geometric Shape of Layout Detection Result <Tip text="Shape mode for layout detection bounding boxes" />
            </div>
            <div style={{ ...radioGroup, marginBottom: '14px' }}>
                {SHAPE_OPTIONS.map(s => (
                    <label key={s} style={radioLabel}>
                        <input type="radio" name="ocrShape" checked={settings.layoutShapeMode === s} onChange={() => set('layoutShapeMode', s)} style={{ accentColor: '#4658ff', width: '14px', height: '14px' }} />
                        {SHAPE_LABELS[s]}
                    </label>
                ))}
            </div>

            {/* Prompt Type */}
            <div style={{ fontSize: '12px', color: '#140e35', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                Prompt Type Setting <Tip text="Recognition mode for VLM inference" />
            </div>
            <div style={radioGroup}>
                {PROMPT_OPTIONS.map(p => (
                    <label key={p.value} style={radioLabel}>
                        <input type="radio" name="ocrPrompt" checked={settings.promptLabel === p.value} onChange={() => handlePromptChange(p.value)} style={{ accentColor: '#4658ff', width: '14px', height: '14px' }} />
                        {p.label}
                    </label>
                ))}
            </div>

            <div style={divider} />

            {/* VLM Sliders */}
            <SliderField label="Repetition Suppression Strength" value={settings.repetitionPenalty} onChange={v => set('repetitionPenalty', v)} min={0.1} max={3} step={0.01} tip="Prevents repetitive content in VLM output" />
            <SliderField label="Recognition Stability" value={settings.temperature} onChange={v => set('temperature', v)} min={0} max={2} step={0.01} tip="Controls generation randomness (lower = more stable)" />
            <SliderField label="Result Credible Range" value={settings.topP} onChange={v => set('topP', v)} min={0} max={1} step={0.1} tip="Top-P nucleus sampling parameter" />
            <SliderField label="Minimum Total Image Pixels" value={settings.minPixels} onChange={v => set('minPixels', v)} min={10000} max={1000000} step={1000} tip="Minimum image resolution for VLM processing" />
            <SliderField label="Maximum Total Image Pixels" value={settings.maxPixels} onChange={v => set('maxPixels', v)} min={100000} max={10000000} step={10000} tip="Maximum image resolution for VLM processing" />

            <div style={divider} />

            {/* NMS + Output */}
            <div style={grid2}>
                <Toggle label="NMS Postprocessing" checked={settings.layoutNms} onChange={v => set('layoutNms', v)} tip="Apply non-maximum suppression for layout detection" />
                <Toggle label="Prettify Markdown" checked={settings.prettifyMarkdown} onChange={v => set('prettifyMarkdown', v)} tip="Format markdown output for better readability" />
                <Toggle label="Format Block Content" checked={settings.formatBlockContent} onChange={v => set('formatBlockContent', v)} tip="Format block content in parsed results" />
                <Toggle label="Show Formula Number" checked={settings.showFormulaNumber} onChange={v => set('showFormulaNumber', v)} tip="Include formula numbers in markdown output" />
            </div>

            <div style={divider} />

            {/* Custom Options */}
            <div style={{ ...sectionTitle, fontSize: '11px', color: '#9498ac' }}>Custom Options</div>
            <div style={grid2}>
                <Toggle label="Force Re-process" checked={settings.force} onChange={v => set('force', v)} tip="Bypass cache and re-process the file" />
                <Toggle label="Skip Image Upload" checked={settings.skipImageUpload} onChange={v => set('skipImageUpload', v)} tip="Skip uploading images to S3 (faster)" />
            </div>

            <div style={divider} />

            {/* Advanced */}
            <button onClick={() => setShowAdvanced(v => !v)} style={{ fontSize: '11px', color: '#4658ff', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 500, display: 'flex', alignItems: 'center', gap: '3px' }}>
                <span style={{ fontSize: '8px', transition: 'transform 0.2s', transform: showAdvanced ? 'rotate(90deg)' : 'none' }}>▶</span>
                Advanced Settings
            </button>
            {showAdvanced && (
                <div style={{ marginTop: '10px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div>
                        <div style={{ fontSize: '11px', color: '#565772', marginBottom: '4px' }}>Layout Threshold</div>
                        <input type="number" value={settings.layoutThreshold ?? ''} onChange={e => set('layoutThreshold', e.target.value === '' ? null : Number(e.target.value))} step={0.05} min={0} max={1} placeholder="null (0.3)" style={{ width: '120px', padding: '3px 6px', border: '1px solid #d9dee9', borderRadius: '4px', fontSize: '11px', color: '#140e35' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: '11px', color: '#565772', marginBottom: '4px' }}>Layout Unclip Ratio</div>
                        <input type="text" value={settings.layoutUnclipRatio ?? ''} onChange={e => set('layoutUnclipRatio', e.target.value === '' ? null : e.target.value)} placeholder="null ([1.0,1.0])" style={{ width: '120px', padding: '3px 6px', border: '1px solid #d9dee9', borderRadius: '4px', fontSize: '11px', color: '#140e35' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: '11px', color: '#565772', marginBottom: '4px' }}>Min Image Size for S3</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input type="range" value={settings.minImageSizeForUpload ?? 5000} onChange={e => set('minImageSizeForUpload', Number(e.target.value))} min={0} max={50000} step={1000} style={{ width: '90px', accentColor: '#4658ff' }} />
                            <span style={{ fontSize: '11px', color: '#140e35' }}>{((settings.minImageSizeForUpload ?? 5000) / 1024).toFixed(0)}KB</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export const DEFAULT_SETTINGS = {
    useLayoutDetection: true,
    useChartRecognition: true,
    useSealRecognition: true,
    useOcrForImageBlock: true,
    useDocPreprocessor: true,
    useDocUnwarping: true,
    layoutNms: true,
    layoutShapeMode: 'auto',
    promptLabel: 'ocr',
    repetitionPenalty: 1.0,
    temperature: 0,
    topP: 1.0,
    minPixels: 147384,
    maxPixels: 2822400,
    prettifyMarkdown: true,
    formatBlockContent: true,
    showFormulaNumber: true,
    visualize: false,
    layoutThreshold: null,
    layoutUnclipRatio: null,
    mergeTables: true,
    relevelTitles: true,
    force: false,
    skipImageUpload: false,
    minImageSizeForUpload: 5000,
    auxiliary: { header: true, header_image: true, footer: true, footer_image: true, number: true, footnote: true, formula_number: true, aside_text: true },
};

export function settingsToParams(settings) {
    const aux = settings.auxiliary || {};
    const markdownIgnoreLabels = AUXILIARY_ITEMS.filter(item => !(aux[item.id] ?? true)).map(item => item.id);
    const params = {
        useLayoutDetection: settings.useLayoutDetection,
        useChartRecognition: settings.useChartRecognition,
        useSealRecognition: settings.useSealRecognition,
        useOcrForImageBlock: settings.useOcrForImageBlock,
        useDocPreprocessor: settings.useDocPreprocessor,
        useDocUnwarping: settings.useDocUnwarping,
        layoutNms: settings.layoutNms,
        layoutShapeMode: settings.layoutShapeMode,
        promptLabel: settings.promptLabel,
        repetitionPenalty: settings.repetitionPenalty,
        temperature: settings.temperature,
        topP: settings.topP,
        minPixels: settings.minPixels,
        maxPixels: settings.maxPixels,
        prettifyMarkdown: settings.prettifyMarkdown,
        formatBlockContent: settings.formatBlockContent,
        showFormulaNumber: settings.showFormulaNumber,
        mergeTables: settings.mergeTables,
        relevelTitles: settings.relevelTitles,
        force: settings.force,
        skipImageUpload: settings.skipImageUpload,
        minImageSizeForUpload: settings.minImageSizeForUpload,
    };
    if (markdownIgnoreLabels.length > 0) params.markdownIgnoreLabels = JSON.stringify(markdownIgnoreLabels);
    if (settings.layoutThreshold != null) params.layoutThreshold = settings.layoutThreshold;
    if (settings.layoutUnclipRatio != null) params.layoutUnclipRatio = settings.layoutUnclipRatio;
    if (settings.mergeTables || settings.relevelTitles) params.restructurePages = true;
    return params;
}
